import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { ConsultantConfirmationForm } from 'common/forms';
import { ConsultantConfirmationFormValues } from 'common/models/Forms';

import { QuestionTitle, PageControls } from '../../../../components';
import {
  selectProtocolConsultantConfirmation,
  setProtocolConsultantConfirmationValuesAction,
  selectInvestingAgreements,
  syncInvestingAgreementsWithProtocolAction,
} from '../../../../ducks';
import { useFormStepContinueRef } from '../../../../hooks/useFormStepContinueRef';
import { FeatureProps } from '../../../../index';

export default function ProtocolConsultantConfirmationFeature({ isPrivileged }: FeatureProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialValues = useSelector(selectProtocolConsultantConfirmation);
  const agreementValues = useSelector(selectInvestingAgreements);

  const { bindFormik, continueWithSoftValidation, showSoftContinueHint } =
    useFormStepContinueRef<ConsultantConfirmationFormValues>();

  function handleBackClick() {
    navigate(`/form/${id}/protocol/customer-confirmation`);
  }

  async function handleContinueClick() {
    await continueWithSoftValidation((values) => {
      handleSubmit(values);
    });
  }

  function bindSubmit(formik: FormikProps<ConsultantConfirmationFormValues>) {
    bindFormik(formik);
  }

  function handleSubmit(values: ConsultantConfirmationFormValues) {
    if (!isPrivileged) {
      dispatch(setProtocolConsultantConfirmationValuesAction(values));
      dispatch(syncInvestingAgreementsWithProtocolAction(values.b));
    }
    navigate(`/form/${id}/agreement`);
  }

  return (
    <>
      <QuestionTitle>{t('protocolFeature.consultantConfirmation')}</QuestionTitle>
      <ConsultantConfirmationForm
        initialValues={{
          a: initialValues?.a,
          b: {
            place: initialValues?.b?.place ?? '',
            date: agreementValues?.dateOfSignature ?? '',
            interviewDuration: initialValues?.b?.interviewDuration ?? agreementValues?.interviewTime ?? '',
            attendants: initialValues?.b?.attendants ?? '',
            city: initialValues?.b?.city ?? agreementValues?.placeOfSignature ?? '',
          },
          c: initialValues?.c,
        }}
        bindSubmit={bindSubmit}
        onSubmit={handleSubmit}
      />
      <PageControls softContinueHint={showSoftContinueHint} onBack={handleBackClick} onContinue={handleContinueClick} />
    </>
  );
}
